<template>
  <TheMenuLayout>
    <v-overlay :value="overlay">
      <div @click="overlay = false" style="cursor: zoom-out;">
        <v-img height="600" width="600" contain :src="overlayImageUrl" />
      </div>
    </v-overlay>
    <TheBreadcrumbs :items="breadcrumbItems" />

    <BaseTitle class="mb-8">
      {{ currentTest.name }}
    </BaseTitle>
    <BaseParagraph class="text-center" v-if="currentTest.limit_date">
      <b v-if="isLate" class="warning--text">
        En retard
      </b>
      <b v-else> Temps restant : {{ remainingTime }} minutes </b>
    </BaseParagraph>

    <v-row class="pl-lg-8">
      <v-item-group v-model="window" class="d-flex flex-column shrink mr-lg-6" mandatory tag="div">
        <v-item
          v-for="(question, idx) in currentTest.questions"
          :key="question.id"
          v-slot="{ active, toggle }"
          class="mb-lg-8 mb-4"
        >
          <div>
            <v-btn
              :input-value="active"
              icon
              @click="
                updateStudentTest();
                toggle();
              "
            >
              <v-icon color="success" v-if="isAnswered(question)">
                mdi-text-box-check
              </v-icon>
              <v-avatar v-else class="border">
                {{ idx + 1 }}
              </v-avatar>
            </v-btn>
          </div>
        </v-item>
      </v-item-group>

      <v-col class="max-w-90">
        <v-window v-model="window" class="elevation-1" vertical>
          <v-window-item v-for="(question, index) in currentTest.questions" :key="question.id">
            <BaseFlatCard>
              <v-card-title> {{ index + 1 }}. <span v-html="questionTitle(question)" /> </v-card-title>
              <v-card-text>
                <div v-html="question.ck_content" />
              </v-card-text>
              <v-img
                contain
                class="mx-auto"
                v-if="question.image_url"
                :src="question.image_url"
                max-height="450"
              />
              <v-divider />
              <v-list-item-group v-if="!question.written_answer" v-model="answer_ids" multiple class="mb-4">
                <template v-for="(possibleAnswer, i) in sortAnswers(question.possible_answers)">

                  <v-list-item
                    :key="`answer-${i}`"
                    :value="possibleAnswer.id"
                    active-class="primary--text text--accent-4"
                  >
                    <template v-slot:default="{ active }">
                      <v-list-item-content>
                        <v-list-item-title class="white-space-no-wrap"><div v-html="possibleAnswer.text"/>
                        </v-list-item-title>
                        <v-img
                          v-if="possibleAnswer.image_url"
                          max-height="200"
                          max-width="200"
                          contain
                          style="cursor: zoom-in"
                          :src="possibleAnswer.image_url"
                          @click.prevent.stop="
                            overlayImageUrl = possibleAnswer.image_url;
                            overlay = !overlay;
                          "
                        />
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-checkbox :input-value="active" color="primary accent-4" />
                      </v-list-item-action>
                    </template>
                  </v-list-item>

                  <v-divider :key="i" v-if="i < question.possible_answers.length - 1" />
                </template>
              </v-list-item-group>

              <v-list-item v-if="question.written_answer">
                <v-textarea class="mt-4" outlined label="Réponse à rédiger" v-model="writtenAnswerFor(question.id).answer"/>
              </v-list-item>
              <v-card-actions>
                <v-btn block color="primary" @click="goToNextQuestion">
                  <template v-if="index < currentTest.questions.length - 1">
                    Confirmer
                  </template>
                  <template v-else-if="isConcoursBlancs">
                    Finir le concours blanc
                  </template>
                  <template v-else>
                    Finir le QCM
                  </template>
                </v-btn>
              </v-card-actions>
            </BaseFlatCard>
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>
  </TheMenuLayout>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import {
  every, includes, excludes, some, sortBy,
} from 'lodash';
import moment from 'moment';
import TheMenuLayout from '@/components/TheMenuLayout.vue';
import BaseTitle from '@/components/BaseTitle.vue';
import BaseParagraph from '@/components/BaseParagraph.vue';
import TheBreadcrumbs from '@/components/TheBreadcrumbs.vue';
import BaseFlatCard from '@/components/BaseFlatCard.vue';

export default {
  name: 'Test',
  components: {
    TheMenuLayout,
    BaseTitle,
    TheBreadcrumbs,
    BaseParagraph,
    BaseFlatCard,
  },
  data() {
    return {
      window: 0,
      remainingTime: '',
      isLate: false,
      overlay: false,
      overlayImageUrl: '',
    };
  },
  created() {
    this.fetchCurrentTest(this.$route.params.id).then(() => {
      if (this.studentTest.finished_at) {
        if (this.isConcoursBlancs) {
          this.$router.push(`/concours_blancs/${this.$route.params.id}/resultat`);
        } else if (this.isPastPaper) {
          this.$router.push(`/annales/${this.$route.params.id}/resultat`);
        } else {
          this.$router.push(`/qcm/${this.$route.params.id}/resultat`);
        }
      }
      this.$nextTick(() => {
        window.MathJax.typeset();
      });
      this.calculateRemainingTime();
      setInterval(() => {
        this.calculateRemainingTime();
      }, 30000);
    });
  },
  methods: {
    ...mapActions(['fetchCurrentTest', 'updateStudentTest']),
    questionTitle(question) {
      let { title } = question;
      if (question.keep_previous_title) {
        const index = this.currentTest.questions.indexOf(question);
        for (let ind = index - 1; ind >= 0; ind -= 1) {
          if (!this.currentTest.questions[ind].keep_previous_title) {
            title = this.currentTest.questions[ind].title;
            break;
          }
        }
      }
      return title;
    },
    calculateRemainingTime() {
      this.remainingTime = moment(this.currentTest.limit_date).diff(moment(), 'minutes');
      if (moment(this.currentTest.limit_date).isBefore(moment())) {
        this.isLate = true;
      }
    },
    isCorrect(question) {
      if (!this.answer_ids.length) {
        return false;
      }
      return every(question.possible_answers, (possibleAnswer) => {
        if (possibleAnswer.correct) {
          return includes(this.answer_ids, possibleAnswer.id);
        }
        return excludes(this.answer_ids, possibleAnswer.id);
      });
    },
    isAnswered(question) {
      if (question.written_answer) {
        return this.student_test_written_answers.find((writtenAnswer) => writtenAnswer.question_id === question.id).answer.length > 0;
      }
      return some(question.possible_answers, (possibleAnswer) => includes(this.answer_ids, possibleAnswer.id));
    },
    goToNextQuestion() {
      this.window += 1;
      this.updateStudentTest();
      if (this.window >= this.currentTest.questions.length) {
        if (this.isConcoursBlancs) {
          this.$router.push(`/concours_blancs/${this.$route.params.id}/resultat`);
        } else if (this.isPastPaper) {
          this.$router.push(`/annales/${this.$route.params.id}/resultat`);
        } else {
          this.$router.push(`/qcm/${this.$route.params.id}/resultat`);
        }
      }
    },
    sortAnswers(answers) {
      return sortBy(answers, 'text');
    },
    writtenAnswerFor(questionId) {
      return this.student_test_written_answers.find((writtenAnswer) => writtenAnswer.question_id === questionId);
    },
  },
  computed: {
    ...mapState(['currentTest', 'studentTest']),
    ...mapFields(['studentTest.answer_ids', 'studentTest.student_test_written_answers']),
    isConcoursBlancs() {
      return this.currentTest.white_test === true;
    },
    isPastPaper() {
      return this.currentTest.past_paper;
    },
    breadcrumbItems() {
      return [
        {
          text: this.breadCrumbText,
          disabled: false,
          href: this.breadCrumbHref,
        },
        {
          text: this.currentTest.name,
          disabled: true,
          href: `/test/${this.currentTest.id}`,
        },
      ];
    },
    breadCrumbText() {
      if (this.isConcoursBlancs) {
        return 'Concours blancs';
      } if (this.isPastPaper) {
        return 'Annales';
      }
      return 'QCMs';
    },
    breadCrumbHref() {
      if (this.isConcoursBlancs) {
        return '/concours_blancs';
      } if (this.isPastPaper) {
        return '/annales';
      }
      return '/qcm';
    },
  },
};
</script>

<style lang="scss" scoped>
.white-space-no-wrap {
  white-space: normal;
}

.max-w-90 {
  max-width: 90%;
}

.border {
  border: 1px solid black;
}
</style>
